import { clsx } from "clsx";
import type { ChangeEvent, KeyboardEvent } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { Text } from "../common/typography";
import { RequiredMark } from "../common/required-mark";

type SelectProps = {
  name: string;
  label?: string;
  placeholder?: string;
  value?: string;
  onChange?: (e: ChangeEvent<HTMLSelectElement>) => void;
  options: Array<{ value: string; label: string }>;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  className?: string;
  density?: "default" | "dense" | "compact";
  fullWidth?: boolean;
  searchable?: boolean;
};

export function Select({
  label,
  placeholder,
  disabled = false,
  name,
  required,
  error,
  value = "",
  onChange,
  helperText,
  className,
  density = "default",
  fullWidth = true,
  options,
  searchable = false,
}: SelectProps) {
  const hasError = Boolean(error);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [search, setSearch] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selectedIndex = options.findIndex((option) => option.value === value);
  const selectedLabel = selectedIndex >= 0 ? options[selectedIndex]?.label : "";
  const displayedLabel = selectedLabel || placeholder || "Select";

  const filteredOptions = useMemo(() => {
    if (!searchable || !search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search, searchable]);

  useEffect(() => {
    if (!isOpen) {
      setActiveIndex(-1);
      setSearch("");
      return;
    }

    if (searchable) {
      requestAnimationFrame(() => searchRef.current?.focus());
    }

    const onMouseDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown as unknown as EventListener);

    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown as unknown as EventListener);
    };
  }, [isOpen, searchable]);

  const commitValue = (nextValue: string) => {
    onChange?.({
      target: { value: nextValue },
      currentTarget: { value: nextValue },
    } as unknown as ChangeEvent<HTMLSelectElement>);
    setIsOpen(false);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) {
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setIsOpen((current) => !current);
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
        return;
      }
      setActiveIndex((current) => (current + 1) % filteredOptions.length);
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        setActiveIndex(selectedIndex >= 0 ? selectedIndex : filteredOptions.length - 1);
        return;
      }
      setActiveIndex((current) => (current <= 0 ? filteredOptions.length - 1 : current - 1));
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      setIsOpen(false);
    }
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) => (current + 1) % filteredOptions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) => (current <= 0 ? filteredOptions.length - 1 : current - 1));
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (activeIndex >= 0 && activeIndex < filteredOptions.length) {
        commitValue(filteredOptions[activeIndex].value);
      }
    } else if (event.key === "Escape") {
      event.preventDefault();
      setIsOpen(false);
    }
  };

  return (
    <div
      ref={rootRef}
      className={clsx("flex flex-col gap-1.5", fullWidth ? "w-full" : "w-auto")}
    >
      {label ? (
        <label htmlFor={name} className="px-1.5">
          <Text variant="bodySmBold" inline tone="body">
            {label}
            {required ? <RequiredMark /> : null}
          </Text>
        </label>
      ) : null}

      <div className="relative">
        <button
          type="button"
          id={name}
          aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-invalid={hasError || undefined}
            disabled={disabled}
            onClick={() => setIsOpen((current) => !current)}
            onKeyDown={handleKeyDown}
            className={clsx(
              "flex w-full items-center justify-between gap-3 rounded-2xl border bg-bgWhite px-3 text-left outline-none transition-colors",
            density === "compact"
              ? "h-10 py-2.5"
              : density === "dense"
                ? "h-12 py-3"
                : "h-[54px] py-4",
            "text-bodySm font-bold text-textDark",
            "focus:border-textBody",
            hasError ? "border-accentError" : "border-borderDefault",
            disabled && "cursor-not-allowed opacity-60",
            className,
          )}
        >
          <span
            className={clsx(
              "min-w-0 flex-1 truncate",
              selectedLabel ? "font-bold text-textDark" : "font-normal text-textBody",
            )}
          >
            {displayedLabel}
          </span>
          {isOpen ? (
            <IconChevronUp size={18} className="shrink-0 text-textBody" />
          ) : (
            <IconChevronDown size={18} className="shrink-0 text-textBody" />
          )}
        </button>

        {isOpen ? (
          <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-50 overflow-hidden rounded-2xl border border-borderDefault bg-bgWhite shadow-[0_28px_70px_rgba(4,13,35,0.16)]">
            {searchable && (
              <div className="border-b border-borderDefault px-3 py-2">
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setActiveIndex(0); }}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Type to search…"
                  className="w-full border-0 bg-transparent text-bodySm text-textDark outline-none placeholder:text-textMuted"
                />
              </div>
            )}
            <div className="max-h-72 overflow-auto p-2" role="listbox" aria-labelledby={name}>
              {placeholder && !searchable ? (
                <button
                  type="button"
                  className={clsx(
                    "flex w-full items-center rounded-xl px-3 text-left text-bodySm text-textBody transition-colors hover:bg-bgLight",
                    density === "compact" ? "py-2.5" : density === "dense" ? "py-2.5" : "py-3",
                  )}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    commitValue("");
                  }}
                  onMouseEnter={() => setActiveIndex(-1)}
                >
                  {placeholder}
                </button>
              ) : null}

              {filteredOptions.length === 0 && searchable ? (
                <div className="px-3 py-3">
                  <Text variant="bodySm" tone="muted">No results</Text>
                </div>
              ) : null}

              {filteredOptions.map((option, index) => {
                const active = index === activeIndex || option.value === value;

                return (
                  <button
                    key={option.value}
                    type="button"
                    className={clsx(
                      "flex w-full items-center rounded-xl px-3 text-left transition-colors",
                      density === "compact" ? "py-2.5" : density === "dense" ? "py-2.5" : "py-3",
                      active ? "bg-[#EEF4FF]" : "hover:bg-[#F4F7FB]",
                    )}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      commitValue(option.value);
                    }}
                    onMouseEnter={() => setActiveIndex(index)}
                  >
                    <span className={clsx("truncate text-bodySm", active ? "text-textDark" : "text-textBody")}>
                      {option.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>

      {hasError ? (
        <span className="px-3">
          <Text variant="bodySmBold" inline tone="error">
            {error}
          </Text>
        </span>
      ) : helperText ? (
        <span className="px-3">
          <Text variant="bodySm" tone="muted" inline>
            {helperText}
          </Text>
        </span>
      ) : null}
    </div>
  );
}
